/**
 * Spec 018 US5 (T042–T044) — spin-replay frame extraction.
 *
 * Turns one `ReplayedOutcome`'s `state_path` into a sequence of
 * visual frames, one per reel draw (`spin` / `spin_respecting_locks`
 * step). Each frame carries everything the `ReplayPanel` needs to
 * render the wild mechanics:
 *
 * - **Sticky**: `lockedMask` is the pre-stamp locked-position mask the
 *   draw respected (`spin_respecting_locks` detail, FR-016) — these
 *   cells render the persistent locked-cell indicator. `newlyLocked`
 *   marks cells a `lock_position` op added AFTER this frame's draw.
 * - **Walking**: `shift` is the `(d_reel, d_row)` a `shift_positions`
 *   op applied just before this frame's draw (the dsl-ops.md walking
 *   order is shift → stamp → evaluate → lock), so the locked cells
 *   visibly move between consecutive frames.
 * - **Expanding**: `expandedReels` lists reels an `expand_to_reel` op
 *   filled on this frame — the single-spin reel-fill flash.
 *
 * The frame grid comes from the `evaluate` step's per-evaluate
 * `detail.grid` (spec 006.3), which already reflects any expansion
 * (expand runs before evaluate). Frames without an evaluate fall back
 * to the previous frame's grid, then the outcome's end-of-spin grid.
 */

import type { ReplayedOutcome, StatePathStep } from "./types";

export const GRID_REELS = 5;
export const GRID_ROWS = 3;

export interface ReplayFrame {
  /** 0-based frame index within the replayed outcome. */
  index: number;
  /** State-machine state the draw ran in (e.g. `free_spins`). */
  state: string;
  /** Whether the draw respected a lock collection. */
  spinKind: "spin" | "spin_respecting_locks";
  /** 5x3 reel-major grid (`grid[reel][row]`) as evaluated. */
  grid: string[][];
  /** Sum of `evaluate` wins on this frame, or null if none fired. */
  win: number | null;
  /**
   * Pre-stamp locked-position mask (bit `reel*3 + row`) the draw
   * respected. 0 for a plain `spin`.
   */
  lockedMask: number;
  /** Cells a `lock_position` op locked after this frame's draw. */
  newlyLocked: Array<[number, number]>;
  /** Reels an `expand_to_reel` op filled on this frame. */
  expandedReels: number[];
  /** Shift a `shift_positions` op applied before this frame's draw. */
  shift: { dReel: number; dRow: number } | null;
}

/** True when `(reel, row)` is set in a packed locked-position mask. */
export function isCellLocked(mask: number, reel: number, row: number): boolean {
  return (mask & (1 << (reel * GRID_ROWS + row))) !== 0;
}

const SPIN_KINDS = new Set(["spin", "spin_respecting_locks"]);

export function extractReplayFrames(outcome: ReplayedOutcome): ReplayFrame[] {
  const frames: ReplayFrame[] = [];
  let pendingShift: { dReel: number; dRow: number } | null = null;
  let current: ReplayFrame | null = null;

  for (const step of outcome.state_path) {
    const detail = step.detail ?? {};
    if (SPIN_KINDS.has(step.action_kind)) {
      current = {
        index: frames.length,
        state: step.state,
        spinKind: step.action_kind as ReplayFrame["spinKind"],
        grid: [],
        win: null,
        lockedMask:
          step.action_kind === "spin_respecting_locks"
            ? (asNumber(detail["locked_mask"]) ?? 0)
            : 0,
        newlyLocked: [],
        expandedReels: [],
        shift: pendingShift,
      };
      pendingShift = null;
      frames.push(current);
    } else if (step.action_kind === "shift_positions") {
      pendingShift = {
        dReel: asNumber(detail["d_reel"]) ?? 0,
        dRow: asNumber(detail["d_row"]) ?? 0,
      };
    } else if (step.action_kind === "expand_to_reel" && current != null) {
      const reel = asNumber(detail["reel"]);
      if (reel != null) current.expandedReels.push(reel);
    } else if (step.action_kind === "evaluate" && current != null) {
      const grid = asGrid(detail["grid"]);
      if (grid != null) current.grid = grid;
      const win = asNumber(detail["win"]);
      if (win != null) current.win = (current.win ?? 0) + win;
    } else if (step.action_kind === "lock_position" && current != null) {
      for (const pos of asPositions(detail["added"])) {
        current.newlyLocked.push(pos);
      }
    }
  }

  // Grid fallbacks: a frame without an evaluate inherits the previous
  // evaluated frame's grid; the final fallback is the outcome's
  // end-of-spin grid.
  let lastGrid: string[][] | null = null;
  for (const frame of frames) {
    if (frame.grid.length > 0) lastGrid = frame.grid;
    else frame.grid = lastGrid ?? outcome.grid;
  }

  if (frames.length === 0) {
    // Degenerate state_path (e.g. a GPU-synthesised minimal path):
    // still show the end-of-spin grid as a single static frame.
    frames.push({
      index: 0,
      state: outcome.state_path[0]?.state ?? "base_game",
      spinKind: "spin",
      grid: outcome.grid,
      win: outcome.winnings,
      lockedMask: 0,
      newlyLocked: [],
      expandedReels: [],
      shift: null,
    });
  }
  return frames;
}

const WILD_ACTIVITY_KINDS = new Set([
  "lock_position",
  "shift_positions",
  "expand_to_reel",
  "spin_respecting_locks",
]);

/**
 * Pick the most interesting outcome of a sample for the replay view:
 * the first with real wild-mechanic activity, else the first that
 * fired a feature, else the first outcome. Null on an empty sample.
 */
export function pickReplayOutcome(
  outcomes: ReplayedOutcome[],
): { outcome: ReplayedOutcome; index: number } | null {
  let featureIdx = -1;
  for (let i = 0; i < outcomes.length; i++) {
    const o = outcomes[i];
    if (o === undefined) continue;
    if (o.state_path.some(hasWildActivity)) return { outcome: o, index: i };
    if (featureIdx < 0 && o.features_fired.length > 0) featureIdx = i;
  }
  if (featureIdx >= 0) {
    const o = outcomes[featureIdx];
    if (o !== undefined) return { outcome: o, index: featureIdx };
  }
  const first = outcomes[0];
  return first !== undefined ? { outcome: first, index: 0 } : null;
}

/**
 * Spec 022 US3 (T032) — the dead-cell sentinel the Megaways / spec-020
 * variable-height materialisation pads short reels with (matching
 * `slot_reference.reels.DEAD_CELL_SENTINEL`). A cell equal to this is
 * *not live* — it must not render and must not count toward the ways
 * count.
 */
export const DEAD_CELL_SENTINEL = "";

/**
 * Trim each reel to its live cells for a Megaways / variable-height
 * board: the spec-020 backing pads rows above a reel's per-spin drawn
 * height with {@link DEAD_CELL_SENTINEL}, always as a trailing run, so
 * dropping trailing sentinels yields each reel's drawn height. A fixed
 * grid (no dead cells) is returned unchanged — this is a no-op there,
 * so it is safe to apply unconditionally before rendering.
 */
export function liveReelGrid(grid: string[][]): string[][] {
  return grid.map((reel) => {
    let end = reel.length;
    while (end > 0 && reel[end - 1] === DEAD_CELL_SENTINEL) end -= 1;
    return reel.slice(0, end);
  });
}

/**
 * The per-spin Megaways ways count — the product of each reel's live
 * cell count (`∏ drawn-symbol populations`, spec 022 AS4). For heights
 * `(4, 6, 3, 7, 2, 5)` this is `4·6·3·7·2·5 = 5040`. Empty (a reel with
 * zero live cells) yields 0.
 */
export function waysCount(grid: string[][]): number {
  if (grid.length === 0) return 0;
  return liveReelGrid(grid).reduce((product, reel) => product * reel.length, 1);
}

/**
 * Whether an execution plan declares a Megaways grid (a per-reel
 * `reel_height_distribution`, drawn per spin). The viewer plan carries
 * the field at the top level (spec 022 wire format); a pre-022 or
 * fixed-grid plan lacks it. Used to gate the per-spin ways-count
 * affordance so it never shows for a lines game.
 */
export function isMegawaysPlan(
  plan: Record<string, unknown> | null | undefined,
): boolean {
  if (plan == null) return false;
  const dists = plan["reel_height_distributions"];
  return dists != null && Array.isArray(dists) && dists.length > 0;
}

function hasWildActivity(step: StatePathStep): boolean {
  if (!WILD_ACTIVITY_KINDS.has(step.action_kind)) return false;
  const detail = step.detail ?? {};
  switch (step.action_kind) {
    case "lock_position":
      return asPositions(detail["added"]).length > 0;
    case "spin_respecting_locks":
      return (asNumber(detail["locked_mask"]) ?? 0) > 0;
    default:
      // shift_positions / expand_to_reel are motion/fill events.
      return true;
  }
}

// ── JSON-shape coercion helpers ────────────────────────────────────

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function asGrid(value: unknown): string[][] | null {
  if (!Array.isArray(value) || value.length === 0) return null;
  const grid: string[][] = [];
  for (const column of value) {
    if (!Array.isArray(column)) return null;
    grid.push(column.map(String));
  }
  return grid;
}

function asPositions(value: unknown): Array<[number, number]> {
  if (!Array.isArray(value)) return [];
  const positions: Array<[number, number]> = [];
  for (const entry of value) {
    if (
      Array.isArray(entry) &&
      entry.length === 2 &&
      typeof entry[0] === "number" &&
      typeof entry[1] === "number"
    ) {
      positions.push([entry[0], entry[1]]);
    }
  }
  return positions;
}
